/**
 * Treppan Academy - Course Detail
 * Course overview with lessons list and progress tracking
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  BookOpen, Clock, Play, Check, Lock, Trophy, Target,
  GraduationCap, ChevronRight, FileText, Video, FileImage,
  Award, ArrowRight, CheckCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const colors = {
  background: '#0A0D14',
  surface: '#131825',
  surfaceHover: '#1C2438',
  gold: '#D4AF37',
  goldHover: '#B5952F',
  success: '#10B981',
  fail: '#EF4444',
  textHeading: '#FFFFFF',
  textBody: '#94A3B8',
  textMuted: '#64748B',
  border: 'rgba(255, 255, 255, 0.08)'
};

const contentTypeIcons = {
  text: FileText,
  video: Video,
  pdf: FileImage
};

const LessonItem = ({ lesson, index, isCompleted, isLocked, isCurrent, onClick }) => {
  const Icon = contentTypeIcons[lesson.content_type] || FileText;
  
  return (
    <div 
      className={`flex items-center gap-4 p-4 rounded-xl transition-all duration-200 ${
        isLocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-white/5'
      } ${isCurrent ? 'ring-2' : ''}`}
      style={{ 
        backgroundColor: isCurrent ? `${colors.gold}10` : 'transparent',
        borderColor: isCurrent ? colors.gold : 'transparent'
      }}
      onClick={() => !isLocked && onClick()}
      data-testid={`lesson-item-${lesson.id}`}
    >
      {/* Status Icon */}
      <div 
        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ 
          backgroundColor: isCompleted ? `${colors.success}20` : isLocked ? `${colors.textMuted}20` : `${colors.gold}20`,
          border: `2px solid ${isCompleted ? colors.success : isLocked ? colors.textMuted : colors.gold}`
        }}
      >
        {isCompleted ? (
          <Check className="h-5 w-5" style={{ color: colors.success }} />
        ) : isLocked ? (
          <Lock className="h-4 w-4" style={{ color: colors.textMuted }} />
        ) : (
          <span className="text-sm font-semibold" style={{ color: colors.gold }}>{index + 1}</span>
        )}
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <Icon className="h-4 w-4 flex-shrink-0" style={{ color: colors.textMuted }} />
          <h4 
            className="font-medium truncate"
            style={{ color: isLocked ? colors.textMuted : colors.textHeading }}
          >
            {lesson.title}
          </h4>
        </div>
        {lesson.description && (
          <p className="text-sm truncate" style={{ color: colors.textMuted }}>
            {lesson.description}
          </p>
        )}
      </div>
      
      {/* Duration */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <Clock className="h-4 w-4" style={{ color: colors.textMuted }} />
        <span className="text-sm" style={{ color: colors.textMuted }}>
          {lesson.duration_minutes} min
        </span>
      </div>
      
      {/* Arrow */}
      {!isLocked && (
        <ChevronRight className="h-5 w-5 flex-shrink-0" style={{ color: colors.textMuted }} />
      )}
    </div>
  );
};

export default function CourseDetail() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    fetchCourse();
  }, [courseId]);

  const fetchCourse = async () => {
    try {
      const res = await fetch(`${API_URL}/api/academy/courses/${courseId}?token=${token}`);
      const result = await res.json();
      
      if (result.success) {
        setCourse(result.course);
      } else {
        toast.error('Course not found');
        navigate('/academy/courses');
      }
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  const handleStartCourse = async () => {
    setStarting(true);
    try {
      const res = await fetch(`${API_URL}/api/academy/courses/${courseId}/start?token=${token}`, {
        method: 'POST'
      });
      const result = await res.json();
      
      if (result.success) {
        // Navigate to first lesson
        const firstLesson = sortedLessons[0];
        if (firstLesson) {
          navigate(`/academy/courses/${courseId}/lessons/${firstLesson.id}`);
        }
      }
    } catch (error) {
      console.error('Error starting course:', error);
      toast.error('Failed to start course');
    } finally {
      setStarting(false);
    }
  };

  const handleContinueCourse = () => {
    // Find next incomplete lesson
    const completedLessons = course.user_progress?.completed_lessons || [];
    const nextLesson = sortedLessons.find(l => !completedLessons.includes(l.id));
    
    if (nextLesson) {
      navigate(`/academy/courses/${courseId}/lessons/${nextLesson.id}`);
    } else if (course.exam) {
      // All lessons done, go to exam
      navigate(`/academy/courses/${courseId}/exam`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="text-center">
          <BookOpen className="h-12 w-12 mx-auto mb-4 animate-pulse" style={{ color: colors.gold }} />
          <p style={{ color: colors.textBody }}>Loading course...</p>
        </div>
      </div>
    );
  }

  if (!course) return null;

  const progress = course.user_progress;
  const completedLessons = progress?.completed_lessons || [];
  const progressPercent = progress?.progress_percent || 0;
  const status = progress?.status || 'not_started';
  const sortedLessons = [...(course.lessons || [])].sort((a, b) => a.order - b.order);
  const hasExam = !!course.exam;
  const allLessonsCompleted = completedLessons.length === sortedLessons.length && sortedLessons.length > 0;
  const hasPassed = progress?.exam_attempts?.some(a => a.passed);

  return (
    <div className="min-h-screen p-6 lg:p-8" style={{ backgroundColor: colors.background }}>
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-6 text-sm">
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => navigate('/academy')}
          style={{ color: colors.textMuted }}
        >
          <GraduationCap className="h-4 w-4 mr-1" />
          Academy
        </Button>
        <span style={{ color: colors.textMuted }}>/</span>
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => navigate('/academy/courses')}
          style={{ color: colors.textMuted }}
        >
          Courses
        </Button>
        <span style={{ color: colors.textMuted }}>/</span>
        <span className="truncate" style={{ color: colors.textHeading }}>{course.title}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Hero */}
          <div 
            className="rounded-xl overflow-hidden"
            style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
          >
            <div className="relative h-64">
              <img 
                src={course.thumbnail_url || 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200'}
                alt={course.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
              
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Badge 
                    className="uppercase text-xs tracking-widest"
                    style={{ 
                      backgroundColor: `${colors.gold}20`,
                      color: colors.gold,
                      border: `1px solid ${colors.gold}30`
                    }}
                  >
                    {course.module?.replace('_', ' ')}
                  </Badge>
                  <Badge 
                    className="uppercase text-xs tracking-widest"
                    style={{ 
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      color: colors.textHeading
                    }}
                  >
                    {course.difficulty}
                  </Badge>
                </div>
                
                <h1 
                  className="text-3xl font-bold mb-2"
                  style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
                >
                  {course.title}
                </h1>
                
                <div className="flex items-center gap-6 text-sm" style={{ color: colors.textBody }}>
                  <span className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {course.duration_hours} hours
                  </span>
                  <span className="flex items-center gap-1">
                    <BookOpen className="h-4 w-4" />
                    {sortedLessons.length} lessons
                  </span>
                  {hasExam && (
                    <span className="flex items-center gap-1">
                      <Award className="h-4 w-4" />
                      Certification exam
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <h2 className="font-semibold mb-3" style={{ color: colors.textHeading }}>
                About this course
              </h2>
              <p style={{ color: colors.textBody, lineHeight: 1.7 }}>
                {course.description}
              </p>
              
              {course.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {course.tags.map(tag => (
                    <Badge 
                      key={tag}
                      variant="outline"
                      className="text-xs"
                      style={{ borderColor: colors.border, color: colors.textMuted }}
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Lessons List */}
          <div 
            className="rounded-xl p-6"
            style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 
                className="text-xl font-semibold"
                style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
              >
                Course Content
              </h2>
              <span className="text-sm" style={{ color: colors.textMuted }}>
                {completedLessons.length} / {sortedLessons.length} completed
              </span>
            </div>
            
            <div className="space-y-2">
              {sortedLessons.map((lesson, index) => {
                const isCompleted = completedLessons.includes(lesson.id);
                // Lessons are unlocked if: first lesson, OR previous lesson is completed
                const isLocked = index > 0 && !completedLessons.includes(sortedLessons[index - 1]?.id) && !isCompleted;
                const isCurrent = !isCompleted && !isLocked && 
                  (index === 0 || completedLessons.includes(sortedLessons[index - 1]?.id));
                
                return (
                  <LessonItem 
                    key={lesson.id}
                    lesson={lesson}
                    index={index}
                    isCompleted={isCompleted}
                    isLocked={isLocked}
                    isCurrent={isCurrent}
                    onClick={() => navigate(`/academy/courses/${courseId}/lessons/${lesson.id}`)}
                  />
                );
              })}
            </div>
            
            {/* Exam Section */}
            {hasExam && (
              <div 
                className={`mt-6 p-4 rounded-xl ${allLessonsCompleted ? 'cursor-pointer hover:bg-white/5' : 'opacity-50'}`}
                style={{ 
                  backgroundColor: allLessonsCompleted ? `${colors.gold}10` : 'transparent',
                  border: `2px solid ${allLessonsCompleted ? colors.gold : colors.border}`
                }}
                onClick={() => allLessonsCompleted && navigate(`/academy/courses/${courseId}/exam`)}
                data-testid="exam-section"
              >
                <div className="flex items-center gap-4">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ 
                      backgroundColor: hasPassed ? `${colors.success}20` : `${colors.gold}20`,
                      border: `2px solid ${hasPassed ? colors.success : colors.gold}`
                    }}
                  >
                    {hasPassed ? (
                      <Trophy className="h-6 w-6" style={{ color: colors.success }} />
                    ) : allLessonsCompleted ? (
                      <Award className="h-6 w-6" style={{ color: colors.gold }} />
                    ) : (
                      <Lock className="h-5 w-5" style={{ color: colors.textMuted }} />
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <h4 className="font-semibold" style={{ color: colors.textHeading }}>
                      {course.exam.title}
                    </h4>
                    <p className="text-sm" style={{ color: colors.textMuted }}>
                      {hasPassed 
                        ? 'You passed! View your certificate' 
                        : allLessonsCompleted 
                          ? `${course.exam.question_count} questions • ${course.exam.time_limit_minutes} minutes • ${course.exam.passing_score}% to pass`
                          : 'Complete all lessons to unlock the exam'
                      }
                    </p>
                  </div>
                  
                  {(allLessonsCompleted || hasPassed) && (
                    <ArrowRight className="h-5 w-5" style={{ color: colors.gold }} />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Progress Card */}
          <div 
            className="rounded-xl p-6 sticky top-6"
            style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
          >
            {status === 'completed' && hasPassed ? (
              <>
                <div className="text-center mb-6">
                  <div 
                    className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center"
                    style={{ backgroundColor: `${colors.success}20` }}
                  >
                    <CheckCircle className="h-10 w-10" style={{ color: colors.success }} />
                  </div>
                  <h3 
                    className="text-xl font-bold mb-1"
                    style={{ color: colors.success, fontFamily: 'Outfit, sans-serif' }}
                  >
                    Course Completed!
                  </h3>
                  <p className="text-sm" style={{ color: colors.textMuted }}>
                    You've earned your certificate
                  </p>
                </div>
                
                <Button 
                  className="w-full rounded-full font-semibold"
                  style={{ backgroundColor: colors.gold, color: '#000' }}
                  onClick={() => navigate(`/academy/certificates/${progress.certificate_id}`)}
                  data-testid="view-certificate-btn"
                >
                  <Award className="h-4 w-4 mr-2" />
                  View Certificate
                </Button>
              </>
            ) : (
              <>
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium" style={{ color: colors.textHeading }}>
                      Your Progress
                    </span>
                    <span 
                      className="text-2xl font-bold"
                      style={{ color: colors.gold, fontFamily: 'Outfit, sans-serif' }}
                    >
                      {progressPercent}%
                    </span>
                  </div>
                  <Progress 
                    value={progressPercent}
                    className="h-3"
                    style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                  />
                  <p className="text-sm mt-2" style={{ color: colors.textMuted }}>
                    {completedLessons.length} of {sortedLessons.length} lessons completed
                  </p>
                </div>
                
                {status === 'not_started' ? (
                  <Button 
                    className="w-full rounded-full font-semibold"
                    style={{ backgroundColor: colors.gold, color: '#000' }}
                    onClick={handleStartCourse}
                    disabled={starting}
                    data-testid="start-course-btn"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    {starting ? 'Starting...' : 'Start Course'}
                  </Button>
                ) : (
                  <Button 
                    className="w-full rounded-full font-semibold"
                    style={{ backgroundColor: colors.gold, color: '#000' }}
                    onClick={handleContinueCourse}
                    data-testid="continue-course-btn"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Continue Learning
                  </Button>
                )}
                
                {allLessonsCompleted && hasExam && !hasPassed && (
                  <Button 
                    className="w-full rounded-full font-semibold mt-3"
                    variant="outline"
                    style={{ borderColor: colors.gold, color: colors.gold }}
                    onClick={() => navigate(`/academy/courses/${courseId}/exam`)}
                    data-testid="take-exam-btn"
                  >
                    <Award className="h-4 w-4 mr-2" />
                    Take Certification Exam
                  </Button>
                )}
              </>
            )}
          </div>

          {/* Course Info */}
          <div 
            className="rounded-xl p-6"
            style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
          >
            <h3 className="font-semibold mb-4" style={{ color: colors.textHeading }}>
              Course Details
            </h3>
            
            <div className="space-y-4">
              <div className="flex justify-between">
                <span style={{ color: colors.textMuted }}>Duration</span>
                <span style={{ color: colors.textHeading }}>{course.duration_hours} hours</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: colors.textMuted }}>Lessons</span>
                <span style={{ color: colors.textHeading }}>{sortedLessons.length}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: colors.textMuted }}>Difficulty</span>
                <span className="capitalize" style={{ color: colors.textHeading }}>{course.difficulty}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: colors.textMuted }}>Certification</span>
                <span style={{ color: colors.textHeading }}>{hasExam ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
